class TestimonialsCarousel {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.carousel = document.getElementById(`testimonials-${sectionId}`);
    if (!this.carousel) return;
    this.track = this.carousel.querySelector(".testimonials-track");
    this.originalCards = this.carousel.querySelectorAll(".testimonial-card");
    this.prevButton = this.carousel.querySelector(".nav-button.prev");
    this.nextButton = this.carousel.querySelector(".nav-button.next");
    this.dots = this.carousel.querySelectorAll(".dot");
    this.isMobile = window.innerWidth < 750;
    this.isAnimating = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.isSwiping = false;
    this.init();
  }

  init() {
    this.setupCards();
    this.bindEvents();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.positionCarousel(false);
      });
    }); 
  }

  setupCards() {
    this.track.style.transform = "none";
    this.track.style.transition = "none";
    this.track.style.visibility = "hidden";

    this.track.querySelectorAll(".testimonial-clone").forEach((clone) => {
      clone.remove();
    });

    if (this.originalCards.length <= 1) {
      this.cards = this.originalCards;
      this.currentSlide = 0;
      this.totalSlides = this.originalCards.length;
      return;
    }

    if (this.isMobile) {
      const lastCardClone = this.originalCards[this.originalCards.length - 1].cloneNode(true);
      const firstCardClone = this.originalCards[0].cloneNode(true);
      lastCardClone.classList.add("testimonial-clone", "mobile-clone-before");
      firstCardClone.classList.add("testimonial-clone", "mobile-clone-after");
      this.track.insertBefore(lastCardClone, this.track.firstChild);
      this.track.appendChild(firstCardClone);
      this.cards = this.track.querySelectorAll(".testimonial-card");
      this.currentSlide = 1;
      this.totalSlides = this.originalCards.length;
      return;
    }

    const clonesBefore = Array.from(this.originalCards).map((card) => {
      const clone = card.cloneNode(true);
      clone.classList.add("testimonial-clone", "clone-before");
      return clone;
    });
    const clonesAfter = Array.from(this.originalCards).map((card) => {
      const clone = card.cloneNode(true);
      clone.classList.add("testimonial-clone", "clone-after");
      return clone;
    });

    clonesBefore.forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    clonesAfter.forEach((clone) => this.track.appendChild(clone));
    this.cards = this.track.querySelectorAll(".testimonial-card");
    this.currentSlide = this.originalCards.length;
    this.totalSlides = this.originalCards.length;
  }

  getSlideMetrics() {
    const card = this.originalCards[0] || this.cards[0];
    const cardWidth = card ? card.offsetWidth : 0;
    const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;

    return {
      cardWidth: cardWidth,
      gap: gap,
      slideWidth: cardWidth + gap,
    };
  }

  getTrackOffset() {
    const transform = getComputedStyle(this.track).transform;
    if (!transform || transform === "none") return 0;
    return new DOMMatrix(transform).m41;
  }

  getDesktopOffset() {
    const metrics = this.getSlideMetrics();
    const containerWidth = this.carousel.offsetWidth;
    const centerOffset = containerWidth / 2 - metrics.cardWidth / 2;

    return -(this.currentSlide * metrics.slideWidth - centerOffset);
  }

  centerActiveCard(animate) {
    const targetCard = this.cards[this.currentSlide];
    if (!targetCard) return;

    const containerRect = this.carousel.getBoundingClientRect();
    const cardRect = targetCard.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    const cardCenter = cardRect.left + cardRect.width / 2;
    const newOffset = this.getTrackOffset() - (cardCenter - containerCenter);

    this.track.style.transition = animate ? "transform 0.3s ease" : "none";
    this.track.style.transform = `translateX(${newOffset}px)`;
  }

  applyTrackOffset(animate) {
    if (this.isMobile) {
      this.centerActiveCard(animate);
      return;
    }

    this.track.style.transition = animate ? "transform 0.3s ease" : "none";
    this.track.style.transform = `translateX(${this.getDesktopOffset()}px)`;
  }

  isOutOfInfiniteBounds() {
    if (this.originalCards.length <= 1) return false;

    if (this.isMobile) {
      return this.currentSlide === 0 || this.currentSlide === this.totalSlides + 1;
    }

    return this.currentSlide < this.totalSlides || this.currentSlide >= this.totalSlides * 2;
  }

  normalizeInfiniteSlide() {
    if (this.originalCards.length <= 1) return false;

    if (this.isMobile) {
      if (this.currentSlide === 0) {
        this.currentSlide = this.totalSlides;
        return true;
      }

      if (this.currentSlide === this.totalSlides + 1) {
        this.currentSlide = 1;
        return true;
      }

      return false;
    }

    if (this.currentSlide >= this.totalSlides * 2) {
      this.currentSlide = this.totalSlides + (this.currentSlide - this.totalSlides * 2);
      return true;
    }

    if (this.currentSlide < this.totalSlides) {
      this.currentSlide = this.totalSlides + this.currentSlide;
      return true;
    }

    return false;
  }

  clampCurrentSlide() {
    if (!this.cards.length) return;
    if (this.currentSlide < 0) this.currentSlide = 0;
    if (this.currentSlide > this.cards.length - 1) {
      this.currentSlide = this.cards.length - 1;
    }
  }

  getRealCardIndex() {
    if (this.originalCards.length <= 1) return this.currentSlide;

    if (this.isMobile) {
      if (this.currentSlide === 0) return this.totalSlides - 1;
      if (this.currentSlide === this.totalSlides + 1) return 0;
      return this.currentSlide - 1;
    }

    if (this.currentSlide < this.totalSlides) return this.currentSlide;
    if (this.currentSlide >= this.totalSlides * 2) {
      return this.currentSlide - this.totalSlides * 2;
    }

    return this.currentSlide - this.totalSlides;
  }

  updateDots() {
    const realCardIndex = this.getRealCardIndex();
    this.dots.forEach((dot, index) => {
      dot.classList.toggle("active", index === realCardIndex);
    });
  }

  positionCarousel(animate) {
    this.clampCurrentSlide();
    if (!this.cards.length || !this.cards[this.currentSlide]) return;

    this.applyTrackOffset(animate);
    this.track.style.visibility = "visible";

    if (this.prevButton) this.prevButton.disabled = false;
    if (this.nextButton) this.nextButton.disabled = false;
    this.updateDots();

    if (this.originalCards.length <= 1) return;

    window.clearTimeout(this.normalizeTimer);
    this.normalizeTimer = window.setTimeout(
      () => {
        if (!this.isOutOfInfiniteBounds()) return;

        this.track.style.transition = "none";
        this.normalizeInfiniteSlide();
        this.clampCurrentSlide();
        this.applyTrackOffset(false);
        this.updateDots();
      },
      animate ? 320 : 0,
    );
  }

  movePrevious() {
    if (this.isMobile && this.originalCards.length <= 1) {
      this.currentSlide =
        this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
      return;
    }

    this.currentSlide--;
  }

  moveNext() {
    if (this.isMobile && this.originalCards.length <= 1) {
      this.currentSlide =
        this.currentSlide === this.totalSlides - 1 ? 0 : this.currentSlide + 1;
      return;
    }

    this.currentSlide++;
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => {
        if (this.isAnimating) return;
        this.isAnimating = true;
        this.movePrevious();
        this.positionCarousel(true);
        window.setTimeout(() => {
          this.isAnimating = false;
        }, 350);
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => {
        if (this.isAnimating) return;
        this.isAnimating = true;
        this.moveNext();
        this.positionCarousel(true);
        window.setTimeout(() => {
          this.isAnimating = false;
        }, 350);
      });
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        if (this.isAnimating) return;
        this.isAnimating = true;

        if (this.isMobile) {
          this.currentSlide = this.originalCards.length > 1 ? index + 1 : index;
        } else {
          this.currentSlide = this.totalSlides + index;
        }

        this.positionCarousel(true);
        window.setTimeout(() => {
          this.isAnimating = false;
        }, 350);
      });
    });

    this.bindTouchEvents();

    window.addEventListener("resize", () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 750;

      if (wasMobile !== this.isMobile) {
        this.setupCards();
      }

      window.requestAnimationFrame(() => {
        this.positionCarousel(false);
      });
    });
  }

  bindTouchEvents() {
    const handleTouchStart = (event) => {
      if (this.isAnimating) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isSwiping = true;
    };

    const handleTouchMove = (event) => {
      if (!this.isSwiping) return;
      this.touchEndX = event.touches[0].clientX;
      this.touchEndY = event.touches[0].clientY;
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (!this.isSwiping || this.isAnimating) return;

      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      const minSwipeDistance = 50;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        this.isAnimating = true;

        if (deltaX > 0) {
          this.movePrevious();
        } else {
          this.moveNext();
        }

        this.positionCarousel(true);
        window.setTimeout(() => {
          this.isAnimating = false;
        }, 350);
      }

      this.isSwiping = false;
    };

    this.track.addEventListener("touchstart", handleTouchStart, { passive: false });
    this.track.addEventListener("touchmove", handleTouchMove, { passive: false });
    this.track.addEventListener("touchend", handleTouchEnd, { passive: true });
    this.track.addEventListener("contextmenu", (event) => {
      event.preventDefault();
    });
  }
}

function initTestimonialsCarousels() {
  document.querySelectorAll('[id^="testimonials-"]').forEach((container) => {
    const sectionId = container.id.replace("testimonials-", "");
    if (container.dataset.carouselReady === "true") return;
    container.dataset.carouselReady = "true";
    new TestimonialsCarousel(sectionId);
  });
}

document.addEventListener("DOMContentLoaded", initTestimonialsCarousels);
document.addEventListener("shopify:section:load", initTestimonialsCarousels);
